// Client-side Proof-of-Work solver.
//
// AUTH_PAYMENT_HARDENING_PLAN.md §10.2.i (M-QA-16 resolution):
// both the main-thread fallback and the Worker wrapper import this same
// `solveBits` function so that an identical (nonce, difficulty) pair always
// yields an identical solution, byte-for-byte. Keeping the search loop in a
// single shared module kills the "inlined-blob-string typo" risk — there is
// no second copy of the loop that could drift out of parity.
//
// Algorithm (copied verbatim from §10.2.i):
//   - Iterate `i = 0, 1, 2, …`; let `solution = String(i)`.
//   - `hash = SHA-256(nonce + ':' + solution)` via Web Crypto
//     (`crypto.subtle.digest`). Browser + Worker + Node 20+ all satisfy this.
//   - Count leading zero bits of `hash` (big-endian byte scan). First byte is
//     the most-significant; `Math.clz32(b) - 24` gives leading zeros inside
//     the byte since `b < 256`.
//   - Return the first `solution` whose hash has `>= difficulty` leading
//     zero bits, or `null` if `maxMs` elapses first.
//
// The server (`supabase/functions/anon-signup-challenge/index.ts`) verifies
// by computing the same hash and calling `leadingZeroBits(hex)`; the two
// formulations (byte-scan here, hex-nibble-scan server-side) agree on the
// bit count for every input (see §5.6 and the `verify` action).
//
// Runtime constraints:
//   - Web Crypto only. No Node-only APIs. Works unmodified in Vercel Edge /
//     browser main thread / Web Worker / Node ≥ 20 test runners.
//   - Iteration suffix is `String(i)` (decimal, unbounded); the server does
//     not care about the format, only that `SHA-256(nonce + ':' + solution)`
//     satisfies the difficulty.

/**
 * Search for a string `solution` such that
 * `SHA-256(nonce + ':' + solution)` has at least `difficulty` leading zero
 * bits. Returns the solution string, or `null` if `maxMs` elapses before a
 * solution is found.
 *
 * The main-thread fallback and the Worker wrapper MUST both delegate here —
 * do not re-implement the loop.
 */
export async function solveBits(
  nonce: string,
  difficulty: number,
  maxMs: number,
): Promise<string | null> {
  const enc = new TextEncoder();
  const started = Date.now();
  let i = 0;
  while (Date.now() - started < maxMs) {
    const solution = String(i++);
    const buf = await crypto.subtle.digest('SHA-256', enc.encode(nonce + ':' + solution));
    const bytes = new Uint8Array(buf);
    let bits = 0;
    for (const b of bytes) {
      if (b === 0) { bits += 8; continue; }
      bits += Math.clz32(b) - 24;
      break;
    }
    if (bits >= difficulty) return solution;
  }
  return null;
}
